import { Box, Typography } from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { AdjustmentRow } from './AdjustmentRow';
import { useAdjustments } from './useAdjustments';

/**
 * Manual balance adjustments ledger (D-balance-adjustment, the Phase 5 capstone and
 * the surface that CLOSES Phase 5). The read-only OVERSIGHT view over the manual-
 * adjustment ledger: every admin-recorded credit or debit, with its member, signed
 * amount, reason code, note, time and acting admin. The adjustment itself is taken
 * from the user detail (UserShow); this is the history.
 *
 * A manual adjustment is a ledger-entry CREATE the balance derives from, never a
 * direct balance write, so this view never mutates anything.
 *
 * OPERATIONAL, SWITCHER-SCOPED (D-balance-adjustment): a wallet is per-member and
 * single-market, so the ledger follows the region switcher scope like the chargebacks
 * and cashout queues, NOT a local investigative facet. ROLE-gated to super_admin: a
 * manual adjustment is the highest-trust write, so even a viewer's read-everywhere
 * wildcard must not reach the ledger.
 */

/** The surface without the access wrapper. Exported so tests can mount it directly. */
export const AdjustmentsView = () => {
  const { view, isLoading } = useAdjustments();

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Trust and safety
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Balance adjustments
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Every manual credit or debit recorded against a member's wallet. Each
        one is a ledger entry the balance derives from, never a direct change to
        a balance. This view follows the region switcher scope. Adjustments are
        made from a member's detail.
      </Typography>

      {isLoading || !view ? (
        <Loading />
      ) : (
        <Box data-testid="adjustments-ledger">
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Recorded adjustments ({view.entries.length})
          </Typography>
          {view.entries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No adjustments recorded in scope.
            </Typography>
          ) : (
            view.entries.map((row) => <AdjustmentRow key={row.id} row={row} />)
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /adjustments. ROLE-gated to super_admin only: a manual
 * adjustment moves value directly, so the ledger is the most sensitive financial
 * oversight surface and a viewer's read-everywhere wildcard must not reach it
 * (D-balance-adjustment).
 */
export const Adjustments = () => (
  <RequireAccess roles={['super_admin']}>
    <AdjustmentsView />
  </RequireAccess>
);
